import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardPageFrame from '../components/DashboardPageFrame.jsx';
import ProblemRecordEditor from '../components/ProblemRecordEditor.jsx';
import RiskRecordEditor from '../components/RiskRecordEditor.jsx';
import { getProblems, getRisks, getIssueRiskTransitions } from '../api/dashboardApi.js';
import { DASHBOARD_TABS } from '../constants/tabs.js';
import {
  aggregateStats,
  isItemOverdue,
  isRowCompleted,
  getItemTitle,
} from '../utils/riskStatsConverters.js';
import { formatOwnerLabel } from '../utils/regionRiskConverters.js';
import '../styles/dashboard-risk-stats.css';
import '../styles/dashboard-region-risk.css';


// ================================================================
// サブコンポーネント
// ================================================================

function DonutChart({ normal, overdue, normalColor, overdueColor, onClickNormal, onClickOverdue }) {
  const total = normal + overdue;
  if (total === 0) {
    return (
      <div className="rstats-donut" style={{ background: '#f3f4f6' }}>
        <div className="rstats-donut__inner" />
      </div>
    );
  }
  const overdueDeg = (overdue / total) * 360;
  const style = {
    background: `conic-gradient(${overdueColor} 0deg ${overdueDeg}deg, ${normalColor} ${overdueDeg}deg 360deg)`,
    cursor: 'pointer',
  };
  return (
    <div className="rstats-donut" style={style} onClick={/* istanbul ignore next -- StatsCard は常に onClickOverdue を渡す */ onClickOverdue || onClickNormal}>
      <div className="rstats-donut__inner" />
    </div>
  );
}

function StatsCard({ title, iconClass, total, overdue, overdueRate, valueClass, normalColor, overdueColor, onClickTotal, onClickOverdue }) {
  return (
    <div className="rstats-card">
      <div className="rstats-card__header">
        <span className={`rstats-card__icon ${iconClass}`}>&#9888;</span>
        <h3 className="rstats-card__title">{title}</h3>
      </div>
      <div className="rstats-card__body">
        <div className="rstats-card__numbers">
          <p className="rstats-card__stat-label">総数</p>
          <p
            className={`rstats-card__stat-value ${valueClass} ${/* istanbul ignore next -- 呼び出し元は常にクリック処理を渡す */ onClickTotal ? 'rstats-card__stat-value--clickable' : ''}`}
            onClick={onClickTotal}
          >
            {total}
          </p>
          <p
            className={`rstats-card__overdue ${/* istanbul ignore next -- 同上 */ onClickOverdue ? 'rstats-card__overdue--clickable' : ''}`}
            onClick={onClickOverdue}
          >
            期日超過: {overdue}件 ({overdueRate.toFixed(1)}%)
          </p>
        </div>
        <DonutChart
          normal={total - overdue}
          overdue={overdue}
          normalColor={normalColor}
          overdueColor={overdueColor}
          onClickOverdue={onClickOverdue}
        />
      </div>
    </div>
  );
}

function BreakdownChart({ title, data, normalClass, overdueClass, onRowClick }) {
  const maxTotal = Math.max(1, ...data.map((d) => d.total));

  return (
    <div className="rstats-section">
      <h4 className="rstats-section__title">{title}</h4>
      <div className="rstats-legend">
        <span className="rstats-legend__item">
          <span className={`rstats-legend__dot ${normalClass}`} style={{ backgroundColor: '' }} />
          正常
        </span>
        <span className="rstats-legend__item">
          <span className={`rstats-legend__dot ${overdueClass}`} style={{ backgroundColor: '' }} />
          期日超過
        </span>
      </div>
      {data.length === 0 ? (
        <p className="rstats-message">データがありません</p>
      ) : (
        <div className="rstats-chart">
          {data.map((row) => {
            /* istanbul ignore next -- 行は実データから作るため総数は必ず1件以上 */
            const normalWidth = maxTotal > 0 ? (row.normal / maxTotal) * 100 : 0;
            /* istanbul ignore next -- 同上 */
            const overdueWidth = maxTotal > 0 ? (row.overdue / maxTotal) * 100 : 0;
            const clickable = row.total > 0 && onRowClick;
            return (
              <div
                className={`rstats-chart-row ${/* istanbul ignore next -- 同上。呼び出し元は常に onRowClick を渡す */ clickable ? 'rstats-chart-row--clickable' : ''}`}
                key={row.name}
                onClick={/* istanbul ignore next -- 同上 */ clickable ? () => onRowClick(row.name, row.items) : undefined}
              >
                <span className="rstats-chart-row__label" title={row.name}>{row.name}</span>
                <div className="rstats-chart-row__bar-track">
                  {row.normal > 0 && (
                    <div
                      className={`rstats-chart-row__bar-fill ${normalClass}`}
                      style={{ width: `${normalWidth}%` }}
                    />
                  )}
                  {row.overdue > 0 && (
                    <div
                      className={`rstats-chart-row__bar-fill ${overdueClass}`}
                      style={{ width: `${overdueWidth}%` }}
                    />
                  )}
                </div>
                <span className="rstats-chart-row__counts">
                  {row.total}件 ({row.overdue}超過)
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ================================================================
// 推移分析サブコンポーネント（SVG 折れ線グラフ）
// ================================================================

// ステータス色・ラベル（課題／リスクで共通）
const STATUS_LINE_COLORS = {
  not_started: '#94a3b8',
  in_progress: '#3b82f6',
  done: '#22c55e',
};
const STATUS_LINE_LABELS = {
  not_started: '未着手',
  in_progress: '進行中',
  done: '完了',
};
const STATUS_LINE_KEYS = ['not_started', 'in_progress', 'done'];

// 課題用（共通定義のエイリアス）
const PROBLEM_LINE_COLORS = STATUS_LINE_COLORS;
const PROBLEM_LINE_LABELS = STATUS_LINE_LABELS;
const PROBLEM_KEYS = STATUS_LINE_KEYS;

// リスク用（共通定義のエイリアス）
const RISK_LINE_COLORS = STATUS_LINE_COLORS;
const RISK_LINE_LABELS = STATUS_LINE_LABELS;
const RISK_KEYS = STATUS_LINE_KEYS;

// ステータスカテゴリ分類（バックエンドの risk_stats.py と同じ定義）
const STATUS_CATEGORIES = {
  not_started: new Set(['未着手', '未対応', 'Open', 'TODO']),
  in_progress: new Set(['進行中', '対応中', 'In Progress']),
  done: new Set(['完了', 'Done', 'Closed', 'クローズ']),
};
const STATUS_CATEGORY_LABELS = {
  not_started: '未着手',
  in_progress: '進行中',
  done: '完了',
};

const CHART_PADDING = { top: 20, right: 20, bottom: 36, left: 36 };

// X軸ラベル（"MM-DD" = 5文字 / fontSize 9）が重ならないために必要な最小間隔（viewBox単位）
const X_LABEL_MIN_GAP = 34;

function LineChart({ weeks, weeklyStats, dataPrefix, colors, labels, statusKeys, width = 600, height = 240, onPointClick }) {
  const plotW = width - CHART_PADDING.left - CHART_PADDING.right;
  const plotH = height - CHART_PADDING.top - CHART_PADDING.bottom;

  // データキー（例: "problem_not_started"）を生成
  const dataKeys = statusKeys.map((k) => `${dataPrefix}_${k}`);

  // 使用中のキーだけに絞る
  const activeIdxes = statusKeys.map((_, i) => i).filter((i) =>
    weeks.some((w) => (weeklyStats[w][dataKeys[i]] || 0) > 0),
  );

  // Y軸最大値
  const maxVal = Math.max(
    1,
    ...weeks.flatMap((w) => activeIdxes.map((i) => weeklyStats[w][dataKeys[i]] || 0)),
  );
  const yTicks = [];
  const yStep = maxVal <= 5 ? 1 : Math.ceil(maxVal / 5);
  for (let v = 0; v <= maxVal; v += yStep) yTicks.push(v);
  if (yTicks[yTicks.length - 1] < maxVal) yTicks.push(maxVal);
  const yMax = yTicks[yTicks.length - 1];

  const xStep = weeks.length > 1 ? plotW / (weeks.length - 1) : 0;
  const toX = (idx) => CHART_PADDING.left + idx * xStep;
  const toY = (v) => CHART_PADDING.top + plotH - (v / yMax) * plotH;

  const labelInterval = xStep > 0 ? Math.max(1, Math.ceil(X_LABEL_MIN_GAP / xStep)) : 1;
  const lastIdx = weeks.length - 1;
  // 最新週を必ず表示するため、最終インデックスを基準に逆順で間引く。
  // 先頭は最新側ラベルと十分離れているときのみ表示する。
  const isLabelVisible = (i) => {
    if (i === lastIdx) return true;
    if ((lastIdx - i) % labelInterval === 0) return true;
    return i === 0 && lastIdx % labelInterval !== 0 && (lastIdx % labelInterval) * xStep >= X_LABEL_MIN_GAP;
  };
  const labelAnchor = (i) => (i === 0 ? 'start' : (i === lastIdx ? 'end' : 'middle'));
  const fmtLabel = (w) => w.slice(5);

  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <div className="rstats-linechart-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} className="rstats-linechart-svg" preserveAspectRatio="xMidYMid meet">
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={CHART_PADDING.left} y1={toY(v)} x2={width - CHART_PADDING.right} y2={toY(v)} stroke="#e5e7eb" strokeWidth="1" />
            <text x={CHART_PADDING.left - 6} y={toY(v) + 4} textAnchor="end" fontSize="10" fill="#6b7280">{v}</text>
          </g>
        ))}

        {weeks.map((w, i) => (
          isLabelVisible(i) ? (
            <text key={w} x={toX(i)} y={height - 4} textAnchor={labelAnchor(i)} fontSize="9" fill="#6b7280">{fmtLabel(w)}</text>
          ) : null
        ))}

        {activeIdxes.map((si) => {
          const dk = dataKeys[si];
          const pts = weeks.map((w, i) => `${toX(i)},${toY(weeklyStats[w][dk] || 0)}`);
          return (
            <polyline key={dk} points={pts.join(' ')} fill="none" stroke={colors[statusKeys[si]]} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          );
        })}

        {weeks.map((w, i) => (
          <rect key={w} x={toX(i) - xStep / 2} y={CHART_PADDING.top} width={xStep || plotW} height={plotH} fill="transparent" onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)} />
        ))}

        {activeIdxes.map((si) => {
          const dk = dataKeys[si];
          return weeks.map((w, i) => {
            const val = weeklyStats[w][dk] || 0;
            if (val <= 0) return null;
            return (
              <g key={`${dk}-${w}`} style={/* istanbul ignore next -- 推移分析タブは常に onPointClick を渡す */ onPointClick ? { cursor: 'pointer' } : undefined} onClick={/* istanbul ignore next -- 同上 */ onPointClick ? () => onPointClick(w, statusKeys[si]) : undefined}>
                <circle cx={toX(i)} cy={toY(val)} r="10" fill="transparent" />
                <circle cx={toX(i)} cy={toY(val)} r="2.5" fill={colors[statusKeys[si]]} stroke="#fff" strokeWidth="1" />
              </g>
            );
          });
        })}

        {hoverIdx !== null && (
          <line x1={toX(hoverIdx)} y1={CHART_PADDING.top} x2={toX(hoverIdx)} y2={CHART_PADDING.top + plotH} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4 2" />
        )}
      </svg>

      {hoverIdx !== null && (
        <div className="rstats-linechart-tooltip" style={{ left: `${(toX(hoverIdx) / width) * 100}%` }}>
          <p className="rstats-linechart-tooltip__date">{weeks[hoverIdx]}</p>
          {activeIdxes.map((si) => {
            const val = weeklyStats[weeks[hoverIdx]][dataKeys[si]] || 0;
            return (
              <p className="rstats-linechart-tooltip__row" key={si}>
                <span className="rstats-linechart-tooltip__dot" style={{ backgroundColor: colors[statusKeys[si]] }} />
                {labels[statusKeys[si]]}: <strong>{val}</strong>
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrendAreaCard({ area, weeklyStats, onChartClick }) {
  const weeks = Object.keys(weeklyStats).sort();

  return (
    <div className="rstats-section rstats-trend-card">
      <h4 className="rstats-section__title">{area}</h4>

      {weeks.length === 0 ? (
        <p className="rstats-message">推移データがありません</p>
      ) : (
        <div className="rstats-trend-pair">
          {/* 課題グラフ */}
          <div className="rstats-trend-half">
            <p className="rstats-trend-half__title rstats-trend-half__title--problem">課題ステータス推移</p>
            <div className="rstats-trend-legend">
              {PROBLEM_KEYS.map((k) => (
                <span className="rstats-legend__item" key={k}>
                  <span className="rstats-legend__dot" style={{ backgroundColor: PROBLEM_LINE_COLORS[k] }} />
                  {PROBLEM_LINE_LABELS[k]}
                </span>
              ))}
            </div>
            <LineChart weeks={weeks} weeklyStats={weeklyStats} dataPrefix="problem" colors={PROBLEM_LINE_COLORS} labels={PROBLEM_LINE_LABELS} statusKeys={PROBLEM_KEYS} onPointClick={/* istanbul ignore next -- 推移分析タブは常に onChartClick を渡す */ onChartClick ? (_week, statusKey) => onChartClick(area, 'problem', statusKey) : undefined} />
          </div>

          {/* リスクグラフ */}
          <div className="rstats-trend-half">
            <p className="rstats-trend-half__title rstats-trend-half__title--risk">リスクステータス推移</p>
            <div className="rstats-trend-legend">
              {RISK_KEYS.map((k) => (
                <span className="rstats-legend__item" key={k}>
                  <span className="rstats-legend__dot" style={{ backgroundColor: RISK_LINE_COLORS[k] }} />
                  {RISK_LINE_LABELS[k]}
                </span>
              ))}
            </div>
            <LineChart weeks={weeks} weeklyStats={weeklyStats} dataPrefix="risk" colors={RISK_LINE_COLORS} labels={RISK_LINE_LABELS} statusKeys={RISK_KEYS} onPointClick={/* istanbul ignore next -- 同上 */ onChartClick ? (_week, statusKey) => onChartClick(area, 'risk', statusKey) : undefined} />
          </div>
        </div>
      )}
    </div>
  );
}

function TrendContent({ transitions, onChartClick }) {
  if (!transitions || !transitions.by_area) {
    return <p className="rstats-message">推移データがありません</p>;
  }

  const areas = Object.keys(transitions.by_area).sort();
  if (areas.length === 0) {
    return <p className="rstats-message">推移データがありません</p>;
  }

  return (
    <div className="rstats-trends">
      {transitions.summary && (
        <p className="rstats-trends__summary">
          対象領域数: {transitions.summary.total_areas}
        </p>
      )}
      {areas.map((area) => (
        <TrendAreaCard
          key={area}
          area={transitions.by_area[area].area}
          weeklyStats={transitions.by_area[area].weekly_stats}
          onChartClick={onChartClick}
        />
      ))}
    </div>
  );
}

function DetailModal({ visible, onClose, title, items, type, onItemClick }) {
  if (!visible) return null;

  return (
    <div className="rrisk-modal-overlay" onClick={onClose}>
      <div className="rrisk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rrisk-modal__header">
          <h3 className="rrisk-modal__title">{title}</h3>
          <button className="rrisk-modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </div>
        <div className="rrisk-modal__body">
          {items.length === 0 ? (
            <p className="rrisk-modal__empty">データがありません</p>
          ) : (
            <ul className="rrisk-modal__list">
              {items.map((item, idx) => {
                const itemTitle = getItemTitle(item, type);
                const overdueFlag = isItemOverdue(item);
                const ownerLabel = formatOwnerLabel(item.owner);
                return (
                  <li className="rrisk-modal__item" key={idx}>
                    <div className="rrisk-modal__item-main">
                      <button
                        className="rrisk-modal__link-btn"
                        onClick={() => onItemClick(item)}
                        type="button"
                      >
                        {itemTitle}
                      </button>
                      {overdueFlag && (
                        <span className="rstats-modal-badge rstats-modal-badge--overdue">遅延</span>
                      )}
                      {ownerLabel && (
                        <span className="rrisk-modal__owner">{ownerLabel}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// メインコンポーネント
// ================================================================

export default function DashboardRiskStatsPage() {
  const [problems, setProblems] = useState([]);
  const [risks, setRisks] = useState([]);
  const [transitions, setTransitions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('problems');

  // モーダル state
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalItems, setModalItems] = useState([]);
  const [modalType, setModalType] = useState('problems');

  // レコード編集
  const [editingProblem, setEditingProblem] = useState(null);
  const [editingRisk, setEditingRisk] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [problemData, riskData, transitionData] = await Promise.all([
        getProblems(),
        getRisks(),
        getIssueRiskTransitions(),
      ]);
      setProblems(problemData);
      setRisks(riskData);
      setTransitions(transitionData);
    } catch {
      setError('課題リスク統計データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadData().finally(() => { if (!active) return; });
    return () => { active = false; };
  }, [loadData]);

  const activeProblems = useMemo(
    () => problems.filter((p) => !isRowCompleted(p) && !p.todo),
    [problems],
  );

  const activeRisks = useMemo(
    () => risks.filter((r) => !isRowCompleted(r)),
    [risks],
  );

  const problemStats = useMemo(
    () => aggregateStats(activeProblems, 'ownerRegion', isItemOverdue),
    [activeProblems],
  );

  const riskStats = useMemo(
    () => aggregateStats(activeRisks, 'actor', isItemOverdue),
    [activeRisks],
  );

  const activeData = activeTab === 'problems' ? problemStats : riskStats;
  const isProblems = activeTab === 'problems';
  const isTrends = activeTab === 'trends';

  const openModal = useCallback((title, items, type) => {
    setModalTitle(title);
    setModalItems(items);
    setModalType(type);
    setModalVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setModalItems([]);
  }, []);

  // モーダルのitemsは開いた時点のスナップショットのため、保存・削除後は閉じて再取得し、
  // 古いupdated_at_isoでの再編集（偽409）や削除済みレコードの再表示を防ぐ
  const handleEditorSaved = useCallback(() => {
    closeModal();
    loadData();
  }, [closeModal, loadData]);

  const handleTrendClick = useCallback((area, type, statusKey) => {
    const statusSet = STATUS_CATEGORIES[statusKey];
    /* istanbul ignore if -- statusKey は PROBLEM_KEYS / RISK_KEYS の値のみ */
    if (!statusSet) return;
    const label = STATUS_CATEGORY_LABELS[statusKey];

    if (type === 'problem') {
      const filtered = problems.filter(
        (p) => (p.ownerRegion || '(未設定)') === area && statusSet.has(p.status) && !p.todo,
      );
      openModal(`課題一覧 - ${area} - ${label}`, filtered, 'problems');
    } else {
      const filtered = risks.filter(
        (r) => (r.actor || '(未設定)') === area && statusSet.has(r.status),
      );
      openModal(`リスク一覧 - ${area} - ${label}`, filtered, 'risks');
    }
  }, [problems, risks, openModal]);

  const openItemEditor = useCallback((item) => {
    if (modalType === 'problems') {
      setEditingProblem({ ...item });
    } else {
      setEditingRisk({ ...item });
    }
  }, [modalType]);

  return (
    <DashboardPageFrame
      mainClassName="rstats-main"
      rootClassName="rstats-page"
      secondaryTabActiveIndex={5}
      secondaryTabs={DASHBOARD_TABS}
    >
      <div className="rstats-scroll-area">
        {loading && <p className="rstats-message">読み込み中...</p>}
        {!loading && error && <p className="rstats-message rstats-message--error">{error}</p>}

        {!loading && !error && (
          <>
            {/* 全体統計カード */}
            <div className="rstats-cards">
              <StatsCard
                title="課題統計"
                iconClass="rstats-card__icon--problem"
                total={problemStats.total}
                overdue={problemStats.overdue}
                overdueRate={problemStats.overdueRate}
                valueClass="rstats-card__stat-value--problem"
                normalColor="#3b82f6"
                overdueColor="#ef4444"
                onClickTotal={() => openModal('課題一覧（全件）', problemStats.allItems, 'problems')}
                onClickOverdue={() => openModal('課題一覧（期日超過）', problemStats.overdueItems, 'problems')}
              />
              <StatsCard
                title="リスク統計"
                iconClass="rstats-card__icon--risk"
                total={riskStats.total}
                overdue={riskStats.overdue}
                overdueRate={riskStats.overdueRate}
                valueClass="rstats-card__stat-value--risk"
                normalColor="#10b981"
                overdueColor="#f59e0b"
                onClickTotal={() => openModal('リスク一覧（全件）', riskStats.allItems, 'risks')}
                onClickOverdue={() => openModal('リスク一覧（期日超過）', riskStats.overdueItems, 'risks')}
              />
            </div>

            {/* タブ切替 */}
            <div className="rstats-tabs">
              <button
                className={`rstats-tab ${activeTab === 'problems' ? 'rstats-tab--active' : ''}`}
                onClick={() => setActiveTab('problems')}
                type="button"
              >
                <span className="rstats-tab__icon">&#9888;</span>
                課題統計
              </button>
              <button
                className={`rstats-tab ${activeTab === 'risks' ? 'rstats-tab--active' : ''}`}
                onClick={() => setActiveTab('risks')}
                type="button"
              >
                <span className="rstats-tab__icon">&#9888;</span>
                リスク統計
              </button>
              <button
                className={`rstats-tab ${activeTab === 'trends' ? 'rstats-tab--active' : ''}`}
                onClick={() => setActiveTab('trends')}
                type="button"
              >
                <span className="rstats-tab__icon">&#128200;</span>
                推移分析
              </button>
            </div>

            {isTrends ? (
              <TrendContent transitions={transitions} onChartClick={handleTrendClick} />
            ) : (
              <>
                {/* 領域別 */}
                <BreakdownChart
                  title={isProblems ? '領域ごとの課題統計' : '領域ごとのリスク統計'}
                  data={activeData.byArea}
                  normalClass={isProblems ? 'rstats-chart-row__bar-fill--normal' : 'rstats-chart-row__bar-fill--risk-normal'}
                  overdueClass={isProblems ? 'rstats-chart-row__bar-fill--overdue' : 'rstats-chart-row__bar-fill--risk-overdue'}
                  onRowClick={(name, items) =>
                    openModal(
                      `${isProblems ? '課題' : 'リスク'}一覧 - ${name}`,
                      items,
                      isProblems ? 'problems' : 'risks',
                    )
                  }
                />

                {/* OEM対地別 */}
                <BreakdownChart
                  title={isProblems ? 'OEM対地ごとの課題統計' : 'OEM対地ごとのリスク統計'}
                  data={activeData.byOemRegion}
                  normalClass={isProblems ? 'rstats-chart-row__bar-fill--normal' : 'rstats-chart-row__bar-fill--risk-normal'}
                  overdueClass={isProblems ? 'rstats-chart-row__bar-fill--overdue' : 'rstats-chart-row__bar-fill--risk-overdue'}
                  onRowClick={(name, items) =>
                    openModal(
                      `${isProblems ? '課題' : 'リスク'}一覧 - ${name}`,
                      items,
                      isProblems ? 'problems' : 'risks',
                    )
                  }
                />

                {activeData.byArea.length === 0 && activeData.byOemRegion.length === 0 && (
                  <p className="rstats-message">
                    データがありません
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>

      <DetailModal
        visible={modalVisible}
        title={modalTitle}
        items={modalItems}
        type={modalType}
        onClose={closeModal}
        onItemClick={openItemEditor}
      />

      <ProblemRecordEditor
        initialRow={editingProblem}
        onClose={() => setEditingProblem(null)}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingProblem)}
      />

      <RiskRecordEditor
        initialRow={editingRisk}
        onClose={() => setEditingRisk(null)}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
        open={Boolean(editingRisk)}
      />
    </DashboardPageFrame>
  );
}
